import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Button } from "../../../components/ui/button.tsx";
import { Input } from "../../../components/ui/input.tsx";
import { Label } from "../../../components/ui/label.tsx";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../config/pwaConfig.ts";
import { Plus, Search, ChevronLeft, ChevronRight, Trash2, Edit2, Package, Upload, Download, FileDown } from "lucide-react";
import { Loader } from "../../../components/Loader";

export const ProductsPage = () => {
    const [showBrands, setShowBrands] = useState(false);
  const { authToken = "", id = "", role = "" } = useSelector((state: any) => state.loginData || {});
  const importFileRef = useRef<HTMLInputElement>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [totalProducts, setTotalProducts] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchDebounced, setSearchDebounced] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<any>({
    id: "",
    name: "",
    articleNo: "",
    hsnCode: "",
    unit: "",
    price: "",
    brandId: "",
    gst: "",
    moq: "",
    description: "",
  });
  const [brands, setBrands] = useState<any[]>([]);
  const [importPreview, setImportPreview] = useState<any[]>([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importBrandId, setImportBrandId] = useState<number>(0);
  const [importing, setImporting] = useState(false);

  // Fetch brands from backend
  useEffect(() => {
    if (!id) return;
    const fetchBrands = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}account/${id}/brands`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        setBrands(response.data.brands || []);
      } catch (error) {
        setBrands([]);
      }
    };
    fetchBrands();
  }, [id, authToken]);

  const maxResults = 10;

  const fetchProducts = async (page = 1) => {
    if (!id) return { products: [], totalCount: 0 };
    try {
      const firstResults = (page - 1) * maxResults;
      const response = await axios.get(`${API_BASE_URL}account/${id}/products`, {
        params: { firstResults, maxResults },
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching products:", error);
      return { products: [], totalCount: 0 };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (formData.id) {
        await updateProduct(formData.id, formData);
        toast.success("Product updated successfully");
      } else {
        await createProduct(formData);
        toast.success("Product created successfully");
      }
      const loadProducts = async () => {
        if (searchDebounced) {
          const response = await axios.get(`${API_BASE_URL}account/${id}/products`, {
            params: { firstResults: 0, maxResults: 10000, search: searchDebounced },
            headers: { Authorization: `Bearer ${authToken}` },
          });
          setProducts(response.data.products || []);
          setTotalProducts(response.data.totalCount ?? 0);
        } else {
          const result = await fetchProducts(page);
          setProducts(result.products);
          setTotalProducts(result.totalCount);
        }
      };
      loadProducts();
      setIsDrawerOpen(false);
      resetForm();
    } catch (error) {
      toast.error(formData.id ? "Failed to update product" : "Failed to create product");
      console.error("Failed to submit product:", error);
    }
  };

const createProduct = async (productData: any) => {
  const payload = {
    name: productData.name,
    articleNo: productData.articleNo,
    hsnCode: productData.hsnCode || "",
    unit: productData.unit,
    price: String(productData.price),
    brandId: Number(productData.brandId) || 0,
    gst: String(productData.gst),
    description: productData.description || "",
    moq: parseInt(productData.moq) || 1,
  };
  const response = await axios.post(
    `${API_BASE_URL}account/${id}/product/create`,
    payload,
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return response.data;
};


const updateProduct = async (productId: string, productData: any) => {
  const payload = {
    id: Number(productId),
    name: productData.name,
    articleNo: productData.articleNo,
    hsnCode: productData.hsnCode || "",
    unit: productData.unit,
    price: String(productData.price),
    gst: String(productData.gst),
    brandId: Number(productData.brandId) || 0,
    description: productData.description || "",
    moq: parseInt(productData.moq) || 1,
  };
  const response = await axios.put(
    `${API_BASE_URL}account/${id}/product/update`,
    payload,
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return response.data;
};



  const handleDelete = async (productId: string) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await deleteProduct(productId);
        setProducts(products.filter((product) => product.id !== productId));
        toast.success("Product deleted");
      } catch (error) {
        toast.error("Failed to delete product");
        console.error("Failed to delete product:", error);
      }
    }
  };

  const deleteProduct = async (productId: string) => {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}account/${id}/product/${productId}/delete`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting product:", error);
      throw error;
    }
  };

  useEffect(() => {
    const t = window.setTimeout(() => setSearchDebounced(searchTerm.trim()), 350);
    return () => window.clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (!id || !authToken) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        if (searchDebounced) {
          const response = await axios.get(`${API_BASE_URL}account/${id}/products`, {
            params: { firstResults: 0, maxResults: 10000, search: searchDebounced },
            headers: { Authorization: `Bearer ${authToken}` },
          });
          if (!cancelled) {
            setProducts(response.data.products || []);
            setTotalProducts(response.data.totalCount ?? 0);
          }
        } else {
          const result = await fetchProducts(page);
          if (!cancelled) {
            setProducts(result.products);
            setTotalProducts(result.totalCount);
          }
        }
      } catch {
        if (!cancelled) {
          setProducts([]);
          setTotalProducts(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id, authToken, page, searchDebounced]);

const openDrawerForCreate = () => {
  setFormData({
    id: "",
    name: "",
    articleNo: "",
    hsnCode: "",
    unit: "",
    price: "",
    brandId: "",
    gst: "",
    moq: "",
    description: "",
  });
  setIsCreating(true);
  setIsDrawerOpen(true);
};

  const openDrawerForEdit = (product: any) => {
    setFormData(product);
    setIsCreating(false);
    setIsDrawerOpen(true);
  };



const resetForm = () => {
  setFormData({
    id: "",
    name: "",
    articleNo: "",
    hsnCode: "",
    unit: "",
    price: "",
    brandId: "",
    gst: "",
    moq: "",
    description: "",
  });
};

  const CSV_HEADERS = ["name", "articleNo", "hsnCode", "unit", "price", "gst", "moq", "description", "brand"];

  const handleExportCSV = async () => {
    try {
      // fetch all products
      const res = await axios.get(`${API_BASE_URL}account/${id}/products`, {
        params: { firstResults: 0, maxResults: 10000 },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const allProds: any[] = res.data?.products || [];
      const rows = [CSV_HEADERS.join(",")];
      allProds.forEach((p) => {
        const row = CSV_HEADERS.map((h) => {
          let val: string;
          if (h === "brand") {
            val = brands.find((b: any) => String(b.id) === String(p.brandId))?.name ?? "";
          } else {
            val = String(p[h] ?? "");
          }
          return `"${val.replace(/"/g, '""')}"`;
        });
        rows.push(row.join(","));
      });
      const blob = new Blob([rows.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "products.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Products exported");
    } catch {
      toast.error("Export failed");
    }
  };

  const handleDownloadSampleCSV = () => {
    const sample = [
      CSV_HEADERS.join(","),
      '"Sample Product","ART-001","8414","Nos","1500","18","10","Product description","BrandName"',
    ].join("\n");
    const blob = new Blob([sample], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "products_sample.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportCSV = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const text = await file.text();
    const lines = text.trim().split("\n");
    if (lines.length < 2) { toast.error("CSV is empty"); return; }
    const parseCSVRow = (line: string): string[] => {
      const result: string[] = [];
      let cur = "", inQ = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') { if (inQ && line[i + 1] === '"') { cur += '"'; i++; } else { inQ = !inQ; } }
        else if (ch === ',' && !inQ) { result.push(cur); cur = ""; }
        else cur += ch;
      }
      result.push(cur);
      return result;
    };
    const headers = parseCSVRow(lines[0]).map((h) => h.trim().replace(/^"|"$/g, "").toLowerCase());
    const nameIdx = headers.findIndex((h) => h === "name");
    if (nameIdx === -1) { toast.error("CSV must have a 'name' column"); return; }
    const parsed: any[] = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = parseCSVRow(lines[i]).map((c) => c.replace(/^"|"$/g, "").trim());
      const obj: any = {};
      headers.forEach((h, idx) => { obj[h] = cols[idx] ?? ""; });
      if (!obj.name) continue;
      parsed.push({
        name: obj.name,
        articleNo: obj.articleno || obj.article_no || "",
        hsnCode: obj.hsncode || obj.hsn_code || "",
        unit: obj.unit || "",
        price: obj.price || "0",
        gst: obj.gst || "0",
        moq: parseInt(obj.moq) || 1,
        description: obj.description || "",
        brandId: obj.brand
          ? (brands.find((b: any) => b.name?.toLowerCase() === obj.brand.toLowerCase())?.id ?? 0)
          : 0,
      });
    }
    if (parsed.length === 0) { toast.error("No valid rows found"); return; }
    setImportPreview(parsed);
    setImportBrandId(parsed[0]?.brandId || (brands[0]?.id ?? 0));
    setShowImportModal(true);
  };

  const handleConfirmImport = async () => {
    if (importPreview.length === 0) return;
    setImporting(true);
    try {
      const productsToUpload = importPreview.map((p) => ({ ...p, brandId: importBrandId }));
      const res = await axios.post(
        `${API_BASE_URL}account/${id}/products/bulk-create`,
        { products: productsToUpload },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const { created = 0, failed = 0, errors = [] } = res.data;
      toast.success(`Imported ${created} products${failed > 0 ? `, ${failed} failed` : ""}`);
      if (errors.length > 0) console.warn("Import errors:", errors);
      const result = await fetchProducts(1);
      setProducts(result.products);
      setTotalProducts(result.totalCount);
      setPage(1);
      setShowImportModal(false);
      setImportPreview([]);
    } catch {
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  };



  const totalPages = searchDebounced ? 1 : Math.ceil(totalProducts / maxResults);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="text-[var(--sidebar-primary)]" size={32} />
            Products
          </h1>
          <p className="text-gray-500 text-sm mt-1">Manage and organize your product catalog</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto flex-wrap">
          <Button variant="outline" size="sm" onClick={handleDownloadSampleCSV} className="flex items-center gap-1.5 text-gray-600 border-gray-300">
            <FileDown size={15} /> Sample CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportCSV} className="flex items-center gap-1.5 text-blue-600 border-blue-300 hover:bg-blue-50">
            <Download size={15} /> Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => importFileRef.current?.click()} className="flex items-center gap-1.5 text-green-600 border-green-300 hover:bg-green-50">
            <Upload size={15} /> Import CSV
          </Button>
          <input ref={importFileRef} type="file" accept=".csv" className="hidden" onChange={handleImportCSV} />
          <Button
            onClick={openDrawerForCreate}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={18} />
            New Product
          </Button>
        </div>
      </div>
      {/* Import CSV Brand Selection Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">Import Products</h2>
              <p className="text-sm text-gray-500 mt-0.5">{importPreview.length} products found — select a brand before importing</p>
            </div>
            <div className="px-6 py-4 max-h-60 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
                    <th className="py-2 text-left font-semibold">Name</th>
                    <th className="py-2 text-left font-semibold">Article No</th>
                    <th className="py-2 text-left font-semibold">Price</th>
                    <th className="py-2 text-left font-semibold">GST%</th>
                  </tr>
                </thead>
                <tbody>
                  {importPreview.map((p, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-1.5 text-gray-800 font-medium truncate max-w-[160px]">{p.name}</td>
                      <td className="py-1.5 text-gray-500 font-mono text-xs">{p.articleNo || "-"}</td>
                      <td className="py-1.5 text-gray-700">{p.price}</td>
                      <td className="py-1.5 text-gray-700">{p.gst}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Assign Brand</label>
                <select
                  value={importBrandId}
                  onChange={(e) => setImportBrandId(Number(e.target.value))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-[var(--sidebar-primary)]"
                >
                  <option value={0}>-- No Brand --</option>
                  {brands.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" size="sm" onClick={() => { setShowImportModal(false); setImportPreview([]); }} disabled={importing}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleConfirmImport} disabled={importing} className="btn-primary">
                  {importing ? "Importing..." : `Import ${importPreview.length} Products`}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-3 text-gray-400" size={20} />
        <Input
          placeholder="Search by product name or HSN code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 py-2 border border-gray-300 rounded-lg"
        />
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        {products.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Package className="mx-auto mb-4 text-gray-300" size={48} />
            <p className="font-medium">No products found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  {/* Removed Image column */}
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Product Name</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Article No</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">HSN Code</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Unit</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Price</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Brand</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">GST</th>
                  {/* Removed Specifications and Formula columns */}
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                    {/* Removed Image cell */}
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{product.name || "-"}</td>
                    <td className="px-6 py-4 text-sm">{product.articleNo || "-"}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-mono">{product.hsnCode && product.hsnCode.trim() !== "" ? product.hsnCode : "-"}</td>
                    <td className="px-6 py-4 text-sm">{product.unit || "-"}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 font-semibold">₹{product.price || "-"}</td>
                    <td className="px-6 py-4 text-sm">{brands.find(b => String(b.id) === String(product.brandId))?.name || "-"}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className="px-3 py-1 bg-[var(--sidebar-light)] text-[var(--sidebar-primary)] rounded-full text-xs font-medium">
                        {product.gst || "-"}%
                      </span>
                    </td>
                    {/* Removed Specifications and Formula cells */}
                    <td className="px-6 py-4 text-right space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDrawerForEdit(product)}
                        className="text-[var(--sidebar-primary)] border-[var(--accent-primary)]/30 hover:bg-[var(--sidebar-light)]"
                      >
                        <Edit2 size={14} className="mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(product.id)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 size={14} className="mr-1" />
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {products.length === 0 ? (
          <div className="p-8 text-center text-gray-500 bg-white rounded-lg border border-gray-200">
            <Package className="mx-auto mb-4 text-gray-300" size={48} />
            <p className="font-medium">No products found</p>
          </div>
        ) : (
          products.map((product) => (
            <div
              key={product.id}
              className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 text-lg">{product.name}</p>
                  <p className="text-xs text-gray-500 font-mono mt-1">HSN: {product.hsnCode && product.hsnCode.trim() !== "" ? product.hsnCode : "-"}</p>
                </div>
                <span className="px-2 py-1 bg-[var(--sidebar-light)] text-[var(--sidebar-primary)] rounded-full text-xs font-medium">
                  {product.gst}%
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Price</p>
                  <p className="text-lg font-bold text-gray-900">₹{product.price}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">GST Rate</p>
                  <p className="text-lg font-bold text-gray-900">{product.gst}%</p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => openDrawerForEdit(product)}
                  className="flex-1 btn-primary"
                >
                  <Edit2 size={14} className="mr-1" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(product.id)}
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 size={14} className="mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 bg-white rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600">
            Page <strong>{page}</strong> of <strong>{totalPages}</strong> ({<strong>{totalProducts}</strong>} total products)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="flex items-center gap-2 disabled:opacity-50"
            >
              <ChevronLeft size={18} />
              <span className="hidden sm:inline">Previous</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="flex items-center gap-2 disabled:opacity-50"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight size={18} />
            </Button>
          </div>
        </div>
      )}

      {/* Drawer Form */}
      {isDrawerOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setIsDrawerOpen(false)} />
      )}
      <div
        className={`fixed md:left-1/2 md:top-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 bottom-0 left-0 right-0 md:bottom-auto bg-white z-50 transform transition-all duration-300 w-full md:w-96 md:h-auto max-h-screen md:max-h-[90vh] overflow-y-auto rounded-t-2xl md:rounded-lg ${
          isDrawerOpen ? "translate-y-0 md:scale-100" : "translate-y-full md:scale-0 md:opacity-0 pointer-events-none"
        }`}
      >
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white flex justify-between items-center rounded-t-2xl md:rounded-t-lg">
          <h2 className="text-xl font-bold text-gray-900">
            {isCreating ? "Create Product" : "Edit Product"}
          </h2>
          <button
            onClick={() => setIsDrawerOpen(false)}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 pb-20 md:pb-6">
          <div>
            <Label className="text-gray-700 block mb-2">Product Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter product name"
              required
            />
          </div>
          <div>
            <Label className="text-gray-700 block mb-2">Article No *</Label>
            <Input
              value={formData.articleNo}
              onChange={(e) => setFormData({ ...formData, articleNo: e.target.value })}
              placeholder="Enter article number"
              required
            />
          </div>
          <div>
            <Label className="text-gray-700 block mb-2">HSN Code</Label>
            <Input
              value={formData.hsnCode}
              onChange={(e) => setFormData({ ...formData, hsnCode: e.target.value })}
              placeholder="Enter HSN code (optional)"
            />
          </div>
          <div>
            <Label className="text-gray-700 block mb-2">Unit *</Label>
            {(() => {
              const UNITS = ["Pcs", "Kg", "Gram", "Liter", "ML", "Meter", "CM", "MM", "Inch", "Foot", "Dozen", "Box", "Pair", "Roll", "Sheet", "Other"];
              const isCustom = formData.unit !== "" && !UNITS.slice(0, -1).includes(formData.unit);
              const dropdownValue = isCustom ? "Other" : (formData.unit || "");
              return (
                <>
                  <select
                    value={dropdownValue}
                    onChange={(e) => {
                      if (e.target.value === "Other") {
                        setFormData({ ...formData, unit: "" });
                      } else {
                        setFormData({ ...formData, unit: e.target.value });
                      }
                    }}
                    required={!isCustom}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-2"
                  >
                    <option value="">Select unit</option>
                    {UNITS.map((u) => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                  {(dropdownValue === "Other" || isCustom) && (
                    <Input
                      value={formData.unit}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      placeholder="Enter custom unit"
                      required
                      autoFocus
                    />
                  )}
                </>
              );
            })()}
          </div>
          <div>
            <Label className="text-gray-700 block mb-2">Brand *</Label>
            <select
              value={formData.brandId}
              onChange={e => setFormData({ ...formData, brandId: e.target.value })}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">Select brand</option>
              {brands.map((brand: any) => (
                <option key={brand.id} value={brand.id}>{brand.name}</option>
              ))}
            </select>
          </div>

          {/* Removed Product Image Upload */}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-700 block mb-2">Price *</Label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="₹"
                required
              />
            </div>
            <div>
              <Label className="text-gray-700 block mb-2">GST % *</Label>
              <Input
                type="number"
                value={formData.gst}
                onChange={(e) => setFormData({ ...formData, gst: e.target.value })}
                placeholder="18%"
                required
              />
            </div>
          </div>

          <div>
            <Label className="text-gray-700 block mb-2">MOQ (Min. Order Qty)</Label>
            <Input
              type="number"
              value={formData.moq || ""}
              onChange={(e) => setFormData({ ...formData, moq: e.target.value })}
              placeholder="e.g. 1"
              min={0}
              onWheel={(e) => e.currentTarget.blur()}
            />
          </div>

          <div>
            <Label className="text-gray-700 block mb-2">Description <span className="text-red-500">*</span></Label>
            <textarea
              rows={3}
              value={formData.description || ""}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Enter product description"
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[var(--sidebar-primary)] resize-none"
            />
          </div>


          <div className="flex gap-2 pt-4 border-t md:sticky md:bottom-0 md:bg-white">
            <Button
              type="submit"
              className="flex-1 btn-primary"
            >
              {isCreating ? "Create Product" : "Update Product"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDrawerOpen(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductsPage;
